import { CSSProperties, useEffect, useState } from 'react';
import { TabComponent, TabsComponent } from '@vivli/shared/components';
import { GridApi } from 'ag-grid-community';
import { IDataRequestSummary } from '@vivli/features/data-requests/infrastructure/interface';
import { Size } from '@vivli/shared/theme';
import { ITab } from '@vivli/shared/infrastructure/interface';
import { DataRequestsGridComponent } from './data-requests-grid.component';
import { BehaviorSubject } from 'rxjs';
import { ExportCsvButtonView } from '@vivli/shared/views';

const exportButtonStyle: CSSProperties = {
    right: '10px',
    '@media screen and (maxWidth: 1480px)': {
        position: 'initial'
    }
} as CSSProperties;

const searchInputStyle: CSSProperties = {
    flex: 'auto',
    resize: 'none',
    padding: Size.PADDING,
    borderRadius: '8px',
    borderColor: 'transparent',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 1px 20px, rgba(0, 0, 0, 0.2) 0px 1px 2px',
    color: 'black',
    position: 'absolute',
    top: '20px',
    right: '40px'
};

const containerStyle: CSSProperties = {
    padding: `0 ${Size.INSET_SIZE}px`,
    height: '100%',
    position: 'relative',
    backgroundColor: 'rgb(233, 234, 239)'
};

interface DataRequestsPageComponentProps {
    title?: string;
    subHeader?: string;
    tabs?: ITab<IDataRequestSummary[]>[];
    defaultTab?: string;
    onRowClick?: (dataRequest: IDataRequestSummary) => void;
    onTabClick?: (tab: ITab<IDataRequestSummary[]>) => void;
    showExport?: boolean;
    displayType?: 'default' | 'myrequests';
    hideFloatingFilter?: boolean;
    isPresumedResearcher?: boolean;
}

const exportColumnKeys = [
    'requestId',
    'requestTitle',
    'userName',
    'userEmail',
    'pi',
    'piEmail',
    'status',
    'dateOfLastAction',
    'dataContributorOrganizations'
];

const gridApis: { gridApi: GridApi; id: string }[] = [];

export const DataRequestsPageComponent = ({
    title,
    subHeader,
    tabs,
    defaultTab,
    onRowClick,
    onTabClick,
    showExport,
    displayType,
    hideFloatingFilter,
    isPresumedResearcher
}: DataRequestsPageComponentProps) => {
    const [activeTabs, setActiveTabs] = useState<ITab<IDataRequestSummary[]>[]>(null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    const getExportButton = (tab: ITab<IDataRequestSummary[]>) => {
        if (!showExport) {
            return null;
        }

        const exportBtnStyle: CSSProperties = {
            position: 'absolute',
            right: 0
        };

        return <ExportCsvButtonView style={exportBtnStyle} onClick={() => exportGridData(tab)} />;
    };

    const exportGridData = (tab: ITab<IDataRequestSummary[]>) => {
        const gridApiToUse = gridApis.find((ga) => ga.id === tab.id)?.gridApi;
        const date = new Date();
        const dateStr = `${date.getMonth() + 1}${date.getDate()}${date.getFullYear()}`;
        const titleStr = tab.title.replace(/\s+/g, '_').toLowerCase();
        const fileName = `${titleStr}_data_requests_${dateStr}`;

        gridApiToUse.exportDataAsCsv({ fileName, columnKeys: exportColumnKeys });
    };

    const addGridApi = (gridApi: GridApi, title) => {
        //prevents extra gridApis from being added
        const existingIndex = gridApis.findIndex((api) => api.id === title);
        if (existingIndex >= 0) {
            gridApis[existingIndex] = { gridApi, id: title };
        } else {
            gridApis.push({ gridApi, id: title });
        }
    };

    const handleTabClick = (tab: ITab<IDataRequestSummary[]>) => {
        onTabClick && onTabClick(tab);
    };

    const handleRowClick = (dataRequest: IDataRequestSummary) => {
        onRowClick && onRowClick(dataRequest);
    };

    const handleSearchChange = (searchValue: string) => {
        gridApis?.forEach(async ({ gridApi }) => {
            await gridApi.setColumnFilterModel('customTitle', {
                filterType: 'text',
                type: 'contains',
                filter: searchValue
            });
            gridApi.onFilterChanged();
        });
    };

    useEffect(() => {
        if (!tabs) {
            return;
        }

        setActiveTabs((previousTabs) =>
            tabs.map((t) => {
                const existingTab = previousTabs?.find((tab) => tab.id === t.id);
                const existingCount = existingTab?.countSubject?.getValue?.() ?? existingTab?.count ?? 0;
                const count = t.count ?? existingCount;

                existingTab?.countSubject?.next(count);

                return {
                    ...t,
                    count,
                    countSubject: existingTab?.countSubject ?? new BehaviorSubject(count)
                };
            })
        );
    }, [tabs]);

    useEffect(() => {
        if (!defaultTab || !activeTabs) {
            return;
        }

        const index = activeTabs.findIndex((t) => t.id.toLowerCase() === defaultTab.toLowerCase());

        setActiveTabIndex(index);
    }, [defaultTab, activeTabs]);

    return (
        <div style={containerStyle}>
            <input
                type="text"
                style={searchInputStyle}
                placeholder="Search data requests"
                onChange={(e) => handleSearchChange(e.target.value)}
            />
            <TabsComponent title={title} subTitle={subHeader} defaultTab={activeTabIndex}>
                {activeTabs?.map((tab, i) => {
                    const { title, data, hint, count, countSubject } = tab;
                    return (
                        <TabComponent
                            key={i}
                            title={title}
                            hint={hint}
                            count={count}
                            countSubject={countSubject}
                            onTabClick={() => handleTabClick(tab)}
                            rightHeaderContent={getExportButton(tab)}
                        >
                            <DataRequestsGridComponent
                                isPresumedResearcher={isPresumedResearcher}
                                displayType={displayType}
                                dataRequests={data}
                                onRowClick={handleRowClick}
                                onGridReady={(gridApi) => addGridApi(gridApi, tab.id)}
                                hideFloatingFilter={hideFloatingFilter}
                                onRowCountChange={(rowCount) => {
                                    countSubject?.next(rowCount);
                                }}
                                initialSortField={tab.initialSortField}
                                initialSortOrder={tab.initialSortOrder}
                            />
                        </TabComponent>
                    );
                })}
            </TabsComponent>
        </div>
    );
};
